import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Logo } from '@/components/Logo'

// ── Types ────────────────────────────────────────────────────────────────────

interface BrokerProfile {
  id: string
  name: string
  email: string
  company: string | null
  tier: string
  territory: string
  artistId: string
  parentId: string | null
  children?: SubBrokerSummary[]
}

interface SubBrokerSummary {
  id: string
  name: string
  tier: string
  territory: string
  email: string
  lastLoginAt: string | null
  _count?: { inquiries: number }
}

interface Inquiry {
  id: string
  contactName: string | null
  contactOrg: string | null
  venueName: string | null
  eventDate: string | null
  eventCity: string | null
  type: string
  stage: string
  updatedAt: string
  createdAt: string
  broker?: { id: string; name: string; tier: string }
}

interface BrokerThread {
  id: string
  subject: string
  messageCount: number
  lastActivity: string
  inquiry?: {
    id: string
    contactName: string | null
    contactOrg: string | null
    stage: string
  } | null
}

interface BrokerStats {
  periodDays: number
  activeInquiries: number
  totalInquiries: number
  confirmedDeals: number
  conversionRate: number
  avgResponseHours: number | null
  pipelineValue: number
  staleDeals: number
}

// ── API helper ───────────────────────────────────────────────────────────────

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/$/, '')

async function brokerFetch<T>(path: string): Promise<T> {
  const token = localStorage.getItem('ovation.broker.token')
  if (!token) throw new Error('Not authenticated')

  const res = await fetch(`${API_BASE}/broker-portal${path}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
    },
  })

  if (res.status === 401) {
    localStorage.removeItem('ovation.broker.token')
    localStorage.removeItem('ovation.broker.profile')
    throw new Error('Session expired')
  }

  const payload = await res.json()
  if (!res.ok) throw new Error(payload.error?.message ?? 'Request failed')
  return payload.data
}

// ── Tier badge ───────────────────────────────────────────────────────────────

const TIER_COLORS: Record<string, string> = {
  primary: 'bg-amber-100 text-amber-800',
  secondary: 'bg-blue-100 text-blue-800',
  sub_broker: 'bg-slate-100 text-slate-700',
  independent: 'bg-green-100 text-green-800',
  regional: 'bg-purple-100 text-purple-800',
  specialist: 'bg-pink-100 text-pink-800',
  co_promoter: 'bg-orange-100 text-orange-800',
  exclusive: 'bg-red-100 text-red-800',
}

function TierBadge({ tier }: { tier: string }) {
  const cls = TIER_COLORS[tier] ?? 'bg-gray-100 text-gray-700'
  return (
    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${cls}`}>
      {tier.replace(/_/g, ' ')}
    </span>
  )
}

// ── Stage badge ──────────────────────────────────────────────────────────────

const STAGE_COLORS: Record<string, string> = {
  inquiry: 'bg-gray-100 text-gray-700',
  qualified: 'bg-blue-100 text-blue-700',
  offer_sent: 'bg-indigo-100 text-indigo-700',
  hold: 'bg-yellow-100 text-yellow-800',
  contract_issued: 'bg-purple-100 text-purple-700',
  contract_signed: 'bg-violet-100 text-violet-700',
  deposit_received: 'bg-emerald-100 text-emerald-700',
  confirmed: 'bg-green-100 text-green-800',
  advancing: 'bg-teal-100 text-teal-700',
  completed: 'bg-green-200 text-green-900',
}

function StageBadge({ stage }: { stage: string }) {
  const cls = STAGE_COLORS[stage] ?? 'bg-gray-100 text-gray-700'
  return (
    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${cls}`}>
      {stage.replace(/_/g, ' ')}
    </span>
  )
}

// ── Stat card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-white p-4">
      <p className="text-xs font-medium text-ovation-muted">{label}</p>
      <p className="mt-1 text-2xl font-semibold tracking-tight">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-ovation-muted">{sub}</p>}
    </div>
  )
}

// ── Days in stage ────────────────────────────────────────────────────────────

function daysAgo(dateStr: string): number {
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24))
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '--'
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

// ── Pipeline stages for filter ───────────────────────────────────────────────

const PIPELINE_STAGES = [
  'inquiry',
  'qualified',
  'offer_sent',
  'hold',
  'contract_issued',
  'contract_signed',
  'deposit_received',
  'confirmed',
  'advancing',
  'completed',
]

// ── Main component ───────────────────────────────────────────────────────────

export function BrokerPortalPage() {
  const navigate = useNavigate()

  const [profile, setProfile] = useState<BrokerProfile | null>(null)
  const [stats, setStats] = useState<BrokerStats | null>(null)
  const [pipeline, setPipeline] = useState<Inquiry[]>([])
  const [pipelineMeta, setPipelineMeta] = useState<{ page: number; limit: number; total: number } | null>(null)
  const [subBrokers, setSubBrokers] = useState<SubBrokerSummary[]>([])
  const [threads, setThreads] = useState<BrokerThread[]>([])
  const [stageFilter, setStageFilter] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'pipeline' | 'sub-brokers' | 'threads'>('pipeline')
  const [selectedInquiry, setSelectedInquiry] = useState<Inquiry | null>(null)

  const token = localStorage.getItem('ovation.broker.token')

  // Redirect to login if no token
  useEffect(() => {
    if (!token) {
      navigate('/broker/login', { replace: true })
    }
  }, [token, navigate])

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [profileData, statsData, pipelineData, threadsData] = await Promise.all([
        brokerFetch<BrokerProfile>('/me'),
        brokerFetch<BrokerStats>('/stats'),
        brokerFetch<Inquiry[]>(`/pipeline${stageFilter ? `?stage=${stageFilter}` : ''}`),
        brokerFetch<BrokerThread[]>('/threads'),
      ])
      setProfile(profileData)
      setStats(statsData)
      setPipeline(pipelineData)
      setThreads(threadsData)

      // Load sub-brokers if primary or secondary
      if (['primary', 'secondary'].includes(profileData.tier)) {
        try {
          const subs = await brokerFetch<SubBrokerSummary[]>('/sub-brokers')
          setSubBrokers(subs)
        } catch {
          // Not a fatal error — may not have sub-brokers
        }
      }
    } catch (err) {
      if (err instanceof Error && err.message === 'Session expired') {
        navigate('/broker/login', { replace: true })
        return
      }
      setError(err instanceof Error ? err.message : 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }, [stageFilter, navigate])

  useEffect(() => {
    if (token) loadData()
  }, [token, loadData])

  function handleLogout() {
    localStorage.removeItem('ovation.broker.token')
    localStorage.removeItem('ovation.broker.profile')
    navigate('/broker/login', { replace: true })
  }

  if (!token) return null

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-ovation-paper">
        <div className="text-sm text-ovation-muted">Loading broker portal...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4 bg-ovation-paper">
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
        <button
          onClick={loadData}
          className="rounded-md bg-ovation-ink px-4 py-2 text-sm text-ovation-paper"
        >
          Retry
        </button>
      </div>
    )
  }

  const canViewSubBrokers = profile && ['primary', 'secondary'].includes(profile.tier)

  return (
    <div className="min-h-screen bg-ovation-paper">
      {/* Header */}
      <header className="border-b border-ovation-ink/10 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-4">
            <Logo />
            <div className="hidden border-l border-ovation-ink/10 pl-4 sm:block">
              <p className="text-sm font-semibold">{profile?.name}</p>
              <div className="flex items-center gap-2 text-xs text-ovation-muted">
                {profile?.company && <span>{profile.company}</span>}
                {profile && <TierBadge tier={profile.tier} />}
                {profile?.territory && (
                  <span className="text-ovation-muted">{profile.territory}</span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="rounded-md border border-ovation-ink/15 px-3 py-1.5 text-xs font-medium text-ovation-muted transition-colors hover:bg-ovation-ink/5 hover:text-ovation-ink"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
        {/* Stats row */}
        {stats && (
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatCard
              label="Active inquiries"
              value={String(stats.activeInquiries)}
              sub={`${stats.totalInquiries} total`}
            />
            <StatCard
              label="Avg response time"
              value={stats.avgResponseHours !== null ? `${stats.avgResponseHours}h` : '--'}
              sub="Last 90 days"
            />
            <StatCard
              label="Conversion rate"
              value={`${stats.conversionRate}%`}
              sub={`${stats.confirmedDeals} confirmed`}
            />
            <StatCard
              label="Pipeline value"
              value={stats.pipelineValue > 0
                ? `${(stats.pipelineValue / 100).toLocaleString('en-GB', {
                    style: 'currency',
                    currency: 'GBP',
                    maximumFractionDigits: 0,
                  })}`
                : '--'}
              {...(stats.staleDeals > 0 ? { sub: `${stats.staleDeals} stale` } : {})}
            />
          </div>
        )}

        {/* Tab bar */}
        <div className="mb-4 flex gap-1 rounded-lg border border-ovation-ink/10 bg-white p-1">
          <TabButton
            label="Pipeline"
            active={activeTab === 'pipeline'}
            onClick={() => setActiveTab('pipeline')}
          />
          {canViewSubBrokers && (
            <TabButton
              label={`Sub-brokers (${subBrokers.length})`}
              active={activeTab === 'sub-brokers'}
              onClick={() => setActiveTab('sub-brokers')}
            />
          )}
          <TabButton
            label="Threads"
            active={activeTab === 'threads'}
            onClick={() => setActiveTab('threads')}
          />
        </div>

        {/* Pipeline tab */}
        {activeTab === 'pipeline' && (
          <div className="rounded-xl border border-ovation-ink/10 bg-white">
            {/* Stage filter */}
            <div className="flex items-center gap-2 border-b border-ovation-ink/10 px-4 py-3">
              <span className="text-xs font-medium text-ovation-muted">Filter:</span>
              <select
                value={stageFilter}
                onChange={(e) => setStageFilter(e.target.value)}
                className="rounded-md border border-ovation-ink/15 px-2 py-1 text-xs focus:border-ovation-ink focus:outline-none"
              >
                <option value="">All stages</option>
                {PIPELINE_STAGES.map((s) => (
                  <option key={s} value={s}>
                    {s.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </div>

            {/* Pipeline table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-ovation-ink/5 text-left text-xs font-medium text-ovation-muted">
                    <th className="px-4 py-2.5">Contact</th>
                    <th className="px-4 py-2.5">Venue / City</th>
                    <th className="px-4 py-2.5">Date</th>
                    <th className="px-4 py-2.5">Type</th>
                    <th className="px-4 py-2.5">Stage</th>
                    <th className="px-4 py-2.5">Days</th>
                    <th className="px-4 py-2.5">Last activity</th>
                    {canViewSubBrokers && <th className="px-4 py-2.5">Broker</th>}
                  </tr>
                </thead>
                <tbody>
                  {pipeline.length === 0 ? (
                    <tr>
                      <td colSpan={canViewSubBrokers ? 8 : 7} className="px-4 py-8 text-center text-ovation-muted">
                        No inquiries found.
                      </td>
                    </tr>
                  ) : (
                    pipeline.map((inq) => (
                      <tr
                        key={inq.id}
                        onClick={() => setSelectedInquiry(inq)}
                        className="cursor-pointer border-b border-ovation-ink/5 transition-colors hover:bg-ovation-paper/50 last:border-b-0"
                      >
                        <td className="px-4 py-2.5">
                          <div className="font-medium">{inq.contactName ?? '--'}</div>
                          {inq.contactOrg && (
                            <div className="text-xs text-ovation-muted">{inq.contactOrg}</div>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <div>{inq.venueName ?? '--'}</div>
                          {inq.eventCity && (
                            <div className="text-xs text-ovation-muted">{inq.eventCity}</div>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-xs">{formatDate(inq.eventDate)}</td>
                        <td className="px-4 py-2.5 text-xs capitalize">{inq.type}</td>
                        <td className="px-4 py-2.5">
                          <StageBadge stage={inq.stage} />
                        </td>
                        <td className="px-4 py-2.5 text-xs tabular-nums">{daysAgo(inq.updatedAt)}d</td>
                        <td className="px-4 py-2.5 text-xs text-ovation-muted">
                          {formatDate(inq.updatedAt)}
                        </td>
                        {canViewSubBrokers && (
                          <td className="px-4 py-2.5 text-xs">
                            {inq.broker?.name ?? '--'}
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Sub-brokers tab */}
        {activeTab === 'sub-brokers' && canViewSubBrokers && (
          <div className="rounded-xl border border-ovation-ink/10 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-ovation-ink/5 text-left text-xs font-medium text-ovation-muted">
                    <th className="px-4 py-2.5">Name</th>
                    <th className="px-4 py-2.5">Tier</th>
                    <th className="px-4 py-2.5">Territory</th>
                    <th className="px-4 py-2.5">Email</th>
                    <th className="px-4 py-2.5">Inquiries</th>
                    <th className="px-4 py-2.5">Last login</th>
                  </tr>
                </thead>
                <tbody>
                  {subBrokers.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-ovation-muted">
                        No sub-brokers found.
                      </td>
                    </tr>
                  ) : (
                    subBrokers.map((sub) => (
                      <tr
                        key={sub.id}
                        className="border-b border-ovation-ink/5 last:border-b-0"
                      >
                        <td className="px-4 py-2.5 font-medium">{sub.name}</td>
                        <td className="px-4 py-2.5">
                          <TierBadge tier={sub.tier} />
                        </td>
                        <td className="px-4 py-2.5 text-xs">{sub.territory}</td>
                        <td className="px-4 py-2.5 text-xs text-ovation-muted">{sub.email}</td>
                        <td className="px-4 py-2.5 text-xs tabular-nums">
                          {sub._count?.inquiries ?? '--'}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-ovation-muted">
                          {sub.lastLoginAt ? formatDate(sub.lastLoginAt) : 'Never'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Threads tab */}
        {activeTab === 'threads' && (
          <div className="rounded-xl border border-ovation-ink/10 bg-white">
            <div className="divide-y divide-ovation-ink/5">
              {threads.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-ovation-muted">
                  No email threads found.
                </div>
              ) : (
                threads.map((thread) => (
                  <div key={thread.id} className="flex items-center justify-between px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{thread.subject}</p>
                      <div className="mt-0.5 flex items-center gap-2 text-xs text-ovation-muted">
                        {thread.inquiry && (
                          <>
                            <span>{thread.inquiry.contactName ?? thread.inquiry.contactOrg ?? 'Unknown'}</span>
                            <span className="text-ovation-ink/20">|</span>
                            <StageBadge stage={thread.inquiry.stage} />
                          </>
                        )}
                      </div>
                    </div>
                    <div className="ml-4 flex-shrink-0 text-right">
                      <div className="text-xs font-medium tabular-nums">
                        {thread.messageCount} msg{thread.messageCount !== 1 ? 's' : ''}
                      </div>
                      <div className="text-xs text-ovation-muted">
                        {formatDate(thread.lastActivity)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Inquiry detail modal */}
        {selectedInquiry && (
          <InquiryDetailModal
            inquiry={selectedInquiry}
            onClose={() => setSelectedInquiry(null)}
          />
        )}
      </main>
    </div>
  )
}

// ── Sub-components ───────────────────────────────────────────────────────────

function TabButton({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
        active
          ? 'bg-ovation-ink text-ovation-paper'
          : 'text-ovation-muted hover:bg-ovation-ink/5 hover:text-ovation-ink'
      }`}
    >
      {label}
    </button>
  )
}

function InquiryDetailModal({
  inquiry,
  onClose,
}: {
  inquiry: Inquiry
  onClose: () => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-2xl">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold">{inquiry.contactName ?? 'Unknown Contact'}</h2>
            {inquiry.contactOrg && (
              <p className="text-sm text-ovation-muted">{inquiry.contactOrg}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-ovation-muted transition-colors hover:bg-ovation-ink/5 hover:text-ovation-ink"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <StageBadge stage={inquiry.stage} />
            <span className="text-xs capitalize text-ovation-muted">{inquiry.type}</span>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs font-medium text-ovation-muted">Venue</p>
              <p>{inquiry.venueName ?? '--'}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-ovation-muted">City</p>
              <p>{inquiry.eventCity ?? '--'}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-ovation-muted">Event date</p>
              <p>{formatDate(inquiry.eventDate)}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-ovation-muted">Days in stage</p>
              <p>{daysAgo(inquiry.updatedAt)} days</p>
            </div>
          </div>

          <div className="border-t border-ovation-ink/10 pt-3">
            <p className="text-xs text-ovation-muted">
              Created {formatDate(inquiry.createdAt)} -- Last updated {formatDate(inquiry.updatedAt)}
            </p>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="rounded-md bg-ovation-ink px-4 py-2 text-sm font-medium text-ovation-paper"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
